import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Navigate } from 'react-router-dom';
import CustomDomainRoutes from './components/CustomDomainRoutes';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { EventProvider } from './contexts/EventContext';
import Layout from './components/Layout/Layout';
import MarketingLayout from './components/Marketing/MarketingLayout';
import ProtectedRoute, { AdminRoute } from './components/ProtectedRoute';
import RoleDashboardRedirect from './components/RoleDashboardRedirect';
import ErrorBoundary from './components/ErrorBoundary';

// Public Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AuthCallback from './pages/AuthCallback';
import LandingPage from './pages/LandingPage';
import PublicFormPage from './pages/PublicFormPage';
import Registration from './pages/Registration';
import CompleteRegistration from './pages/CompleteRegistration';
import ShareLinkRedirect from './pages/ShareLinkRedirect';
import SlugRedirect from './pages/SlugRedirect';

// Protected Pages
import Dashboard from './pages/Dashboard';
import Events from './pages/Events';
import CreateEvent from './pages/CreateEvent';
import EventDetails from './pages/EventDetails';
import EventEditor from './pages/EventEditor';
import LeadPipeline from './pages/LeadPipeline';
import AgentSurveyResponses from './pages/AgentSurveyResponses';
import StudentDashboard from './pages/StudentDashboard';
import MyEvents from './pages/MyEvents';
import EventDetailsStudent from './pages/EventDetailsStudent';
import AdminDashboard from './pages/AdminDashboard';
import PaymentHistory from './pages/PaymentHistory';
import Analytics from './pages/Analytics';
import SharingLinks from './pages/SharingLinks';
import StripeConnect from './pages/StripeConnect';
import StripeReturn from './pages/StripeReturn';
import Profile from './pages/Profile';
import MarketingEmailTemplates from './pages/marketing/MarketingEmailTemplates';
import MarketingEmailWorkflows from './pages/marketing/MarketingEmailWorkflows';
import MarketingQuickSend from './pages/marketing/MarketingQuickSend';
import MarketingEmailLogs from './pages/marketing/MarketingEmailLogs';
import PresentationsList from './pages/PresentationsList';
import PresentationViewer from './pages/PresentationViewer';
import PresentationEditor from './pages/PresentationEditor';

import './index.css';

const MarketingFormsList = lazy(() => import('./pages/marketing/MarketingFormsList'));
const MarketingFormsNew = lazy(() => import('./pages/marketing/MarketingFormsNew'));
const MarketingFormsEditor = lazy(() => import('./pages/marketing/MarketingFormsEditor'));
const MarketingFormsResponses = lazy(() => import('./pages/marketing/MarketingFormsResponses'));
const MarketingFormsShare = lazy(() => import('./pages/marketing/MarketingFormsShare'));

const MarketingFormsFallback = () => (
  <div className="flex justify-center py-12">
    <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

function App() {
  return (
    <Router>
      <AuthProvider>
        <EventProvider>
          <div className="min-h-screen bg-gray-50">
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#1e293b',
                  color: '#f1f5f9',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                },
                success: {
                  iconTheme: {
                    primary: '#10b981',
                    secondary: '#f1f5f9',
                  },
                },
                error: {
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#f1f5f9',
                  },
                },
              }}
            />
            <ErrorBoundary>
              <CustomDomainRoutes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />

              {/* Auth Callback Routes (for email confirmations, password resets, etc.) */}
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/auth/reset-password" element={<ResetPassword />} />

              {/* Public Event Pages */}
              <Route path="/landing/:eventId" element={<LandingPage />} />
              <Route path="/forms/:slug" element={<PublicFormPage />} />
              <Route path="/register/:eventId" element={<Registration />} />
              <Route path="/complete-registration/:eventId" element={<CompleteRegistration />} />

              {/* Protected Dashboard Routes */}
              <Route path="/dashboard" element={<Layout />}>
                {/* General Dashboard - accessible to all authenticated users */}
                <Route
                  index
                  element={
                    <ProtectedRoute>
                      <RoleDashboardRedirect fallback={<Dashboard />} />
                    </ProtectedRoute>
                  }
                />

                {/* Event Management - Agents and Admins only */}
                <Route
                  path="events"
                  element={
                    <ProtectedRoute allowedRoles={['agent', 'admin']}>
                      <Events />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="events/create"
                  element={
                    <ProtectedRoute allowedRoles={['agent', 'admin']}>
                      <CreateEvent />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="events/:eventId"
                  element={
                    <ProtectedRoute allowedRoles={['agent', 'admin']}>
                      <EventDetails />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="events/:eventId/edit"
                  element={
                    <ProtectedRoute allowedRoles={['agent', 'admin']}>
                      <EventEditor />
                    </ProtectedRoute>
                  }
                />

                {/* Lead Pipeline - Agents and Admins only */}
                <Route
                  path="leads"
                  element={
                    <ProtectedRoute allowedRoles={['agent', 'admin']}>
                      <LeadPipeline />
                    </ProtectedRoute>
                  }
                />

                {/* Survey / form responses - Agents and Admins only (read-only) */}
                <Route
                  path="surveys"
                  element={
                    <ProtectedRoute allowedRoles={['agent', 'admin']}>
                      <AgentSurveyResponses />
                    </ProtectedRoute>
                  }
                />

                {/* Student Dashboard - Students only */}
                <Route
                  path="student-dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['student']}>
                      <StudentDashboard />
                    </ProtectedRoute>
                  }
                />

                {/* Student My Events - Students only */}
                <Route
                  path="my-events"
                  element={
                    <ProtectedRoute allowedRoles={['student']}>
                      <MyEvents />
                    </ProtectedRoute>
                  }
                />

                {/* Student Event Details - Students only */}
                <Route
                  path="student/events/:eventId"
                  element={
                    <ProtectedRoute allowedRoles={['student']}>
                      <EventDetailsStudent />
                    </ProtectedRoute>
                  }
                />

                {/* Admin Dashboard - Admins only */}
                <Route
                  path="admin"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />

                {/* Analytics - Agents and Admins only */}
                <Route
                  path="analytics"
                  element={
                    <ProtectedRoute allowedRoles={['agent', 'admin']}>
                      <Analytics />
                    </ProtectedRoute>
                  }
                />

                {/* Invitation Links - Agents and Admins only */}
                <Route
                  path="sharing-links"
                  element={
                    <ProtectedRoute allowedRoles={['agent', 'admin']}>
                      <SharingLinks />
                    </ProtectedRoute>
                  }
                />

                {/* Profile - All authenticated users */}
                <Route
                  path="profile"
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  }
                />

                {/* Payment History - All authenticated users */}
                <Route
                  path="payments"
                  element={
                    <ProtectedRoute>
                      <PaymentHistory />
                    </ProtectedRoute>
                  }
                />

                {/* Stripe Connect - Agents only */}
                <Route
                  path="stripe/connect"
                  element={
                    <ProtectedRoute allowedRoles={['agent', 'admin']}>
                      <StripeConnect />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="stripe/return"
                  element={
                    <ProtectedRoute allowedRoles={['agent', 'admin']}>
                      <StripeReturn />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="/agent/dashboard" element={<Layout />}>
                <Route
                  index
                  element={
                    <ProtectedRoute allowedRoles={['agent']}>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="/agent/stripe" element={<Layout />}>
                <Route
                  path="connect"
                  element={
                    <ProtectedRoute allowedRoles={['agent', 'admin']}>
                      <StripeConnect />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="return"
                  element={
                    <ProtectedRoute allowedRoles={['agent', 'admin']}>
                      <StripeReturn />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="/student/dashboard" element={<Layout />}>
                <Route
                  index
                  element={
                    <ProtectedRoute allowedRoles={['student']}>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="/admin/dashboard" element={<Layout />}>
                <Route
                  index
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
              </Route>

              {/* Marketing Dashboard - Admin and Agents */}
              <Route path="/marketing" element={<ProtectedRoute allowedRoles={['admin', 'agent']}><MarketingLayout /></ProtectedRoute>}>
                <Route index element={<Navigate to="/marketing/email/templates" replace />} />
                <Route path="email/templates" element={<MarketingEmailTemplates />} />
                <Route path="email/templates/:templateId" element={<MarketingEmailTemplates />} />
                <Route path="email/workflows" element={<MarketingEmailWorkflows />} />
                <Route path="email/workflows/:workflowId" element={<MarketingEmailWorkflows />} />
                <Route path="email/send" element={<MarketingQuickSend />} />
                <Route path="email/logs" element={<MarketingEmailLogs />} />
                <Route
                  path="forms"
                  element={(
                    <AdminRoute>
                      <Suspense fallback={<MarketingFormsFallback />}>
                        <MarketingFormsList />
                      </Suspense>
                    </AdminRoute>
                  )}
                />
                <Route
                  path="forms/new"
                  element={(
                    <AdminRoute>
                      <Suspense fallback={<MarketingFormsFallback />}>
                        <MarketingFormsNew />
                      </Suspense>
                    </AdminRoute>
                  )}
                />
                <Route
                  path="forms/:formId/edit"
                  element={(
                    <AdminRoute>
                      <Suspense fallback={<MarketingFormsFallback />}>
                        <MarketingFormsEditor />
                      </Suspense>
                    </AdminRoute>
                  )}
                />
                <Route
                  path="forms/:formId/responses"
                  element={(
                    <AdminRoute>
                      <Suspense fallback={<MarketingFormsFallback />}>
                        <MarketingFormsResponses />
                      </Suspense>
                    </AdminRoute>
                  )}
                />
                <Route
                  path="forms/:formId/share"
                  element={(
                    <AdminRoute>
                      <Suspense fallback={<MarketingFormsFallback />}>
                        <MarketingFormsShare />
                      </Suspense>
                    </AdminRoute>
                  )}
                />
              </Route>

              {/* Presentations - Agents and Admins only */}
              <Route path="/presentations" element={<ProtectedRoute allowedRoles={['agent', 'admin']}><Layout /></ProtectedRoute>}>
                <Route index element={<PresentationsList />} />
                <Route path=":id/view" element={<PresentationViewer />} />
                <Route path=":id/edit" element={<PresentationEditor />} />
              </Route>

              {/* Slug / short-link routes - order matters: two segments then one */}
              <Route path="/:eventSlug/:agentSlug" element={<ShareLinkRedirect />} />
              <Route path="/:eventSlug" element={<SlugRedirect />} />
            </CustomDomainRoutes>
            </ErrorBoundary>
          </div>
        </EventProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
